<script setup>
import { onMounted, ref, onBeforeUnmount, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import HeroBanner from '../shared/components/LandingPage/HeroBanner.vue'
import WhyBcsPro from '../shared/components/LandingPage/WhyBcsPro.vue'
import OurProduct from '../shared/components/LandingPage/OurProduct.vue'
import TestimonialsSection from '../shared/components/LandingPage/TestimonialsSection.vue'
import FoundersSection from '../shared/components/LandingPage/FoundersSection.vue'
import FaqsLandingSection from '../shared/components/LandingPage/FaqsLandingSection.vue'
import RequestDemoSalesCompliance from '../shared/components/SalesCompliance/RequestDemoSalesCompliance.vue'
import NavbarSalesCompliance from '../shared/components/SalesCompliance/NavbarSalesCompliance.vue'
import { useHamburguerMenuStore } from '../shared/stores/HamburguerMenu'
import { storeToRefs } from 'pinia'

const isUat = import.meta.env.VITE_PROJECT_TYPE_FIREBASE === 'uat'
const route = useRoute()

const hamburguerMenuStore = useHamburguerMenuStore()
const { isMenuOpen } = storeToRefs(hamburguerMenuStore)
const { closeMenu } = hamburguerMenuStore

const activePage = ref('')

const actionItems = computed(() => {
    const items = [
        { id: 'request-demo', label: 'REQUEST A DEMO', target: 'request-demo', type: 'button', primary: true, showMobile: true }
    ]
    if (isUat) {
        items.push({ id: 'signin', label: 'Sign In', target: '/login', type: 'button', primary: false, showMobile: false })
    }
    return items
})

const navItems = [
    { id: 'why-bcspro', label: 'WHY BCS PRO', target: 'why-bcspro' },
    { id: 'our-product', label: 'OUR PRODUCT', target: 'our-product' },
    { id: 'testimonials', label: 'TESTIMONIALS', target: 'testimonials' },
    { id: 'founders-note', label: 'FOUNDER\'S NOTES', target: 'founders-note' },
    { id: 'FAQs', label: 'FAQS', target: 'FAQs' }
]

const disableScroll = () => {
    document.body.style.overflow = 'hidden';
}

const enableScroll = () => {
    document.body.style.removeProperty('overflow');
}

window.addEventListener('resize', () => {
    if (window.innerWidth > 1030) {
        enableScroll()
        closeMenu()
    }
})

watch(isMenuOpen, (newVal) => {
    if (newVal) {
        disableScroll()
    } else {
        enableScroll()
    }
})

const scrollToElement = (elementId, offset = 0) => {
    const element = document.getElementById(elementId)
    if (element) {
        const offsetTop = element.getBoundingClientRect().top + window.scrollY - offset
        window.scrollTo({ top: offsetTop, behavior: 'smooth' })
        return true
    }
    return false
}


const handleNavClick = (item) => {
    activePage.value = item.id
    if (isMenuOpen.value) {
        closeMenu()
        setTimeout(() => {
            scrollToElement(item.target, 80)
        }, 100)
    } else {
        scrollToElement(item.target, 80)
    }
}

const handleActionClick = (item) => {
    if (item.id === 'request-demo') {
        if (isMenuOpen.value) {
            closeMenu()
            setTimeout(() => {
                scrollToElement('request-demo')
            }, 100)
        } else {
            scrollToElement('request-demo')
        }
    } else if (item.id === 'signin') {
        closeMenu();
        if (item.target.startsWith('/')) {
            window.location.href = item.target + (Object.keys(route.query).length > 0 ? '?' + new URLSearchParams(route.query).toString() : '')
        }
    }
}


const closeMenuFromOverlay = () => {
    closeMenu()
}

const handleScroll = () => {
    if (isMenuOpen.value) return;

    const sections = navItems.map(item => {
        const element = document.getElementById(item.target)
        return { id: item.id, element }
    }).filter(section => section.element !== null)

    if (window.scrollY < 100) {
        activePage.value = ''
        return
    }

    const faqsSection = sections.find(section => section.id === 'FAQs')

    if (faqsSection) {
        const faqsRect = faqsSection.element.getBoundingClientRect()
        if (faqsRect.bottom < 500) {
            activePage.value = ''
            return
        }
    }

    const viewportHeight = window.innerHeight
    let currentSection = null
    let maxVisiblePercentage = 0

    sections.forEach(section => {
        const rect = section.element.getBoundingClientRect()
        const visibleTop = Math.max(0, rect.top)
        const visibleBottom = Math.min(viewportHeight, rect.bottom)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)

        const sectionHeight = rect.height
        const visiblePercentage = (visibleHeight / sectionHeight) * 100

        if ((visiblePercentage > maxVisiblePercentage) ||
            (rect.top <= 100 && rect.bottom >= 100)) {
            maxVisiblePercentage = visiblePercentage
            currentSection = section.id
        }
    })

    if (currentSection) {
        activePage.value = currentSection
    }
}

onMounted(() => {
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    if (isMenuOpen.value) {
        disableScroll();
    }

    if (window.location.hash) {
        const targetId = window.location.hash.substring(1)
        setTimeout(() => {
            scrollToElement(targetId, 80)
        }, 500)
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    if (isMenuOpen.value) {
        enableScroll()
    }
})
</script>

<template>
    <div class="sales-page">
        <NavbarSalesCompliance :activePage="activePage" :navItems="navItems" :actionItems="actionItems"
            @nav-click="handleNavClick" @action-click="handleActionClick" />
        <section>
            <HeroBanner />
        </section>
        <!-- Why BCSPro -->
        <section id="why-bcspro">
            <WhyBcsPro />
        </section>
        <!-- Our Product -->
        <section id="our-product">
            <OurProduct />
        </section>

        <!-- What People Are Saying -->
        <section id="testimonials">
            <TestimonialsSection />
        </section>
        <!-- Founders -->
        <section id="founders-note">
            <FoundersSection />
        </section>

        <section id="FAQs">
            <FaqsLandingSection />
        </section>

        <section id="request-demo">
            <RequestDemoSalesCompliance />
        </section>

    </div>

    <div v-if="isMenuOpen" class="overlay" @click="closeMenuFromOverlay"></div>
</template>

<style>
h3 {
    font-size: 30px;
    font-weight: 500;
}
</style>

<style scoped>
:deep(.hero-banner-container) {
    :deep(.btn.btn-primary) {
        background-color: #2B2B68 !important;
        border-radius: 14px;
        transition: all 0.4s ease;
    }

    :deep(.btn.btn-primary:hover) {
        background-color: #161634 !important;
    }

    :deep(.btn) {
        border-radius: 14px;
        border-color: #2B2B68 !important;
        text-transform: uppercase;
        transition: all 0.4s ease;
    }

}


:deep(.active) {
    background-color: #2B2B68 !important;
    border-left: 4px solid #B9B9D9 !important;
    transition: all 0.4s ease;
}

:deep(.option) {
    color: #2B2B68 !important;
    transition: all 0.4s ease;
}


:deep(.accordion-button:not(.collapsed) strong) {
    color: #2B2B68 !important;
    transition: all 0.4s ease;
}

:deep(.white-space-pre-wrap li::before) {
    color: #2B2B68 !important;
    transition: all 0.4s ease;
}

:deep(.btn-info) {
    background-color: transparent !important;
    color: #2B2B68 !important;
    transition: all 0.4s ease;
}

:deep(.btn-info:hover) {
    background-color: #161634 !important;
    color: var(--bs-white) !important;
}

.what-people-are-saying-container {
    background-color: #2B2B68 !important;

}

:deep(.icon-wrapper) {
    border: 8px solid #2B2B68 !important;
    background-color: #B9B9D9 !important;
    transition: all 0.4s ease;
}

:deep(.accordion-button) {
    transition: all 0.4s ease;

    strong {
        color: #2B2B68 !important;
        transition: all 0.4s ease;
    }
}


@media (max-width: 1030px) {
    .overlay {
        background-color: rgba(255, 255, 255, 0.8);
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 500;
        right: 0;
        bottom: 0;
        cursor: pointer;
    }

    .sales-page {
        position: relative;
    }


}
</style>